import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Code, FileText, Send, Archive } from 'lucide-react';
import { EduArticle, EduDomain, EduSubject, EduKnowledgePoint, CreateArticleRequest, UpdateArticleRequest } from '../../types/learning';
import { API_CONFIG } from '../../config/api';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import HierarchySelector from '../../components/shared/HierarchySelector';

const LearningArticleEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id && id !== 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState<EduDomain[]>([]);
  const [subjects, setSubjects] = useState<EduSubject[]>([]);
  const [knowledgePoints, setKnowledgePoints] = useState<EduKnowledgePoint[]>([]);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  
  const [formData, setFormData] = useState<CreateArticleRequest>({
    title: '',
    summary: '',
    content: '',
    domain_id: '',
    subject_id: '',
    point_id: '',
    author: '',
    cover_image: '',
    status: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchArticle();
    } else {
      setLoading(false);
    }
  }, [id, isEditing]);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchDomains(),
        fetchSubjects(),
        fetchKnowledgePoints()
      ]);
    } catch (error) {
      console.error('获取数据失败:', error);
      toast.error('获取数据失败');
    }
  };

  const fetchArticle = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?action=get&id=${id}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch article');

      const result = await response.json();
      if (result.success && result.data) {
        const article = result.data;
        setFormData({
          title: article.title,
          summary: article.summary || '',
          content: article.content || '',
          domain_id: article.domain_id,
          subject_id: article.subject_id,
          point_id: article.point_id,
          author: article.author || '',
          cover_image: article.cover_image || '',
          status: article.status
        });
      }
    } catch (error) {
      console.error('获取文章失败:', error);
      toast.error('获取文章失败');
      navigate('/admin/learning');
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-domains-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch domains');

      const result = await response.json();
      if (result.success) {
        setDomains(result.data.data || []);
      }
    } catch (error) {
      console.error('获取领域列表失败:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
    }
  };

  const fetchKnowledgePoints = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch knowledge points');

      const result = await response.json();
      if (result.success) {
        setKnowledgePoints(result.data.data || []);
      }
    } catch (error) {
      console.error('获取知识点列表失败:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('请输入文章标题');
      return;
    }

    if (!formData.domain_id || !formData.subject_id || !formData.point_id) {
      toast.error('请选择完整的层级结构');
      return;
    }

    try {
      setSaving(true);
      const url = isEditing 
        ? `${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?action=update`
        : `${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?action=create`;
      
      const body = isEditing 
        ? { ...formData, id: id } as UpdateArticleRequest
        : formData;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to save article');

      const result = await response.json();
      if (result.success) {
        toast.success(isEditing ? '文章更新成功' : '文章创建成功');
        navigate('/admin/learning');
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('保存文章失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = () => {
    setFormData({
      ...formData,
      status: formData.status === 1 ? 0 : 1
    });
  };

  const handleHierarchyChange = (selection: any) => {
    setFormData({
      ...formData,
      domain_id: selection.domain_id || '',
      subject_id: selection.subject_id || '',
      point_id: selection.point_id || ''
    });
  };

  // 处理HTML内容，提取实际的HTML部分
  const processHtmlContent = (content: string) => {
    if (!content) return '';
    
    // 检查是否包含AI生成的标记
    const htmlStartTag = '===HTML_RESULT_START===';
    const htmlEndTag = '===HTML_RESULT_END===';
    const thinkingStartTag = '===THINKING_START===';
    const thinkingEndTag = '===THINKING_END===';
    
    let processedContent = content;
    
    // 如果包含thinking标记，先移除thinking部分
    if (content.includes(thinkingStartTag) && content.includes(thinkingEndTag)) {
      const thinkingStart = content.indexOf(thinkingStartTag);
      const thinkingEnd = content.indexOf(thinkingEndTag) + thinkingEndTag.length;
      processedContent = content.slice(0, thinkingStart) + content.slice(thinkingEnd);
    }
    
    // 如果包含HTML标记，提取HTML部分
    if (processedContent.includes(htmlStartTag) && processedContent.includes(htmlEndTag)) {
      const htmlStart = processedContent.indexOf(htmlStartTag) + htmlStartTag.length;
      const htmlEnd = processedContent.indexOf(htmlEndTag);
      processedContent = processedContent.slice(htmlStart, htmlEnd).trim();
    }
    
    return processedContent;
  };

  const renderPreview = () => {
    const processedContent = processHtmlContent(formData.content);
    
    return (
      <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900 prose-code:text-pink-600 prose-code:bg-pink-50 prose-pre:bg-gray-800 prose-pre:text-gray-100">
        <style>{`
          .article-preview img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          .article-preview table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
            border: 1px solid #e5e7eb;
          }
          .article-preview th,
          .article-preview td {
            border: 1px solid #e5e7eb;
            padding: 0.5rem 1rem;
            text-align: left;
          }
          .article-preview th {
            background-color: #f9fafb;
            font-weight: 600;
          }
          .article-preview blockquote {
            border-left: 4px solid #3b82f6;
            padding-left: 1rem;
            margin: 1rem 0;
            font-style: italic;
            background-color: #f8fafc;
            padding: 1rem;
            border-radius: 4px;
          }
          .article-preview ul, .article-preview ol {
            padding-left: 1.5rem;
          }
          .article-preview li {
            margin-bottom: 0.25rem;
          }
          .article-preview h1, .article-preview h2, .article-preview h3 {
            margin-top: 2rem;
            margin-bottom: 1rem;
          }
          .article-preview p {
            line-height: 1.7;
            margin-bottom: 1rem;
          }
        `}</style>
        <div 
          dangerouslySetInnerHTML={{ __html: processedContent }}
          className="article-preview"
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">加载中...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部操作栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/learning')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                返回列表
              </button>
              <div className="text-lg font-semibold text-gray-900">
                {isEditing ? '编辑文章' : '创建文章'}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* 视图切换按钮 */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('edit')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'edit'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Code className="w-4 h-4 mr-1 inline" />
                  编辑
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'preview'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Eye className="w-4 h-4 mr-1 inline" />
                  预览
                </button>
              </div>
              
              {/* 状态切换按钮 */}
              <button
                onClick={handleToggleStatus}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  formData.status === 1
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                }`}
              >
                {formData.status === 1 ? (
                  <>
                    <Archive className="w-4 h-4 mr-1" />
                    已发布
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1" />
                    草稿
                  </>
                )}
              </button>
              
              {/* 保存按钮 */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左侧：文章基本信息 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                文章信息
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  文章标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入文章标题"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  作者
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入作者姓名"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  层级结构 <span className="text-red-500">*</span>
                </label>
                <HierarchySelector
                  value={{
                    domain_id: formData.domain_id,
                    subject_id: formData.subject_id,
                    point_id: formData.point_id
                  }}
                  onChange={handleHierarchyChange}
                  required={{ domain: true, subject: true, knowledge_point: true }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  封面图片
                </label>
                <div className="space-y-2">
                  <input
                    type="url"
                    value={formData.cover_image}
                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入图片URL"
                  />
                  {formData.cover_image && (
                    <img
                      src={formData.cover_image}
                      alt="封面预览"
                      className="w-full h-32 rounded-lg object-cover"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  文章摘要
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入文章摘要"
                />
              </div>
            </div>
          </div>

          {/* 右侧：内容编辑/预览 */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {viewMode === 'edit' ? '内容编辑' : '内容预览'}
                </h3>
              </div>
              
              <div className="p-6">
                {viewMode === 'edit' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML内容
                    </label>
                    <textarea
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={25}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="请输入HTML内容..."
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      支持HTML格式。如果内容包含AI生成的标记，预览时会自动提取HTML部分。
                    </p>
                  </div>
                ) : (
                  <div className="min-h-[600px] border border-gray-200 rounded-md p-4">
                    {formData.content ? (
                      renderPreview()
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-500">
                        暂无内容可预览
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningArticleEditPage;